import React, { useState, useEffect } from 'react';
import { Moon, Sun, Globe, Github, Linkedin, Instagram, Mail, Code, Terminal, Menu } from 'lucide-react';
import Hero from './sections/Hero';
import About from './sections/About';
import Skills from './sections/Skills';
import Certifications from './sections/Certifications';
import Projects from './sections/Projects';
import Experience from './sections/Experience';
import Contact from './sections/Contact';
import './Portfolio.css';

const Portfolio = ({ darkMode, toggleDarkMode, language, toggleLanguage }) => {
  const [activeSection, setActiveSection] = useState('hero');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const handleEmailClick = async (e) => {
    e.preventDefault();
    const email = 'arthur.pazzutti0@gmail.com';
    try {
      await navigator.clipboard.writeText(email);
      alert(language === 'pt' ? 'Email copiado!' : 'Email copied!');
    } catch (err) {
      window.open(`https://mail.google.com/mail/?view=cm&fs=1&to=${email}`, '_blank');
    }
  };

  // As chaves de `nav` são os próprios ids das seções — é o que permite ao
  // scroll-spy marcar o item ativo comparando `activeSection === key`.
  const translations = {
    pt: {
      nav: {
        hero: 'Início',
        about: 'Sobre',
        skills: 'Habilidades',
        certifications: 'Certificações',
        projects: 'Projetos',
        experience: 'Experiência',
        contact: 'Contato'
      }
    },
    en: {
      nav: {
        hero: 'Home',
        about: 'About',
        skills: 'Skills',
        certifications: 'Certifications',
        projects: 'Projects',
        experience: 'Experience',
        contact: 'Contact'
      }
    }
  };

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setActiveSection(sectionId);
    }
  };

  useEffect(() => {
    const handleScroll = () => {
      const sections = ['hero', 'about', 'skills', 'certifications', 'projects', 'experience', 'contact'];
      const scrollPosition = window.scrollY + 100;

      for (let i = sections.length - 1; i >= 0; i--) {
        const element = document.getElementById(sections[i]);
        if (element && element.offsetTop <= scrollPosition) {
          setActiveSection(sections[i]);
          break;
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className={`portfolio ${darkMode ? 'dark' : 'light'}`}>
      {/* Header/Navigation */}
      <header className="header">
        <div className="container">
          <nav className="nav">
            <div className="nav-left">
                <div className="nav-brand">
                  <Terminal className="nav-icon" />
                  <span className="brand-text">Pazzutti</span>
                </div>
                <div className="nav-controls">
                  <button
                    className="control-btn"
                    onClick={toggleLanguage}
                    title={`Switch to ${language === 'pt' ? 'English' : 'Portuguese'}`}
                  >
                    <Globe size={18} />
                    <span className="control-text">{language.toUpperCase()}</span>
                  </button>

                  <button
                    className="control-btn"
                    onClick={toggleDarkMode}
                    title={`Switch to ${darkMode ? 'light' : 'dark'} mode`}
                  >
                    {darkMode ? <Sun size={18} /> : <Moon size={18} />}
                  </button>

                  <a
                    href="https://github.com/pazzutti"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="control-btn github-link"
                    title="View GitHub Profile"
                  >
                    <Github size={18} />
                  </a>
                </div>
            </div>

            <div className="nav-right">
                <button className="mobile-menu-toggle" onClick={toggleMobileMenu}>
                    <Menu size={24} />
                </button>
                <ul className={`nav-menu ${isMobileMenuOpen ? 'open' : ''}`}>
                  {Object.entries(translations[language].nav).map(([key, value]) => (
                    <li key={key}>
                      <button
                        className={`nav-link ${activeSection === key ? 'active' : ''}`}
                        onClick={() => {
                            scrollToSection(key);
                            if (isMobileMenuOpen) {
                                toggleMobileMenu();
                            }
                        }}
                      >
                        {value}
                      </button>
                    </li>
                  ))}
                </ul>
            </div>
          </nav>
        </div>
      </header>

      {/* Main Content */}
      <main>
        <Hero language={language} />
        <About language={language} />
        <Skills language={language} />
        <Certifications language={language} />
        <Projects language={language} />
        <Experience language={language} />
        <Contact language={language} />
      </main>

      {/* Footer */}
      <footer className="footer">
        <div className="container">
          <div className="footer-content">
            <div className="footer-brand">
              <div className="footer-logo">
                <Code className="footer-icon" />
                <span className="footer-brand-text">Pazzutti</span>
              </div>
              <p className="footer-description">
                {language === 'pt' 
                  ? 'Desenvolvedor de Software apaixonado por criar soluções inovadoras.'
                  : 'Software Developer passionate about creating innovative solutions.'
                }
              </p>
            </div>

            <div className="footer-links">
              <div className="footer-section">
                <h4 className="footer-title">
                  {language === 'pt' ? 'Links Rápidos' : 'Quick Links'}
                </h4>
                <ul className="footer-list">
                  <li><button onClick={() => scrollToSection('about')} className="footer-link">
                    {translations[language].nav.about}
                  </button></li>
                  <li><button onClick={() => scrollToSection('certifications')} className="footer-link">
                    {translations[language].nav.certifications}
                  </button></li>
                  <li><button onClick={() => scrollToSection('projects')} className="footer-link">
                    {translations[language].nav.projects}
                  </button></li>
                  <li><button onClick={() => scrollToSection('contact')} className="footer-link">
                    {translations[language].nav.contact}
                  </button></li>
                </ul>
              </div>

              <div className="footer-section">
                <h4 className="footer-title">
                  {language === 'pt' ? 'Conecte-se' : 'Connect'}
                </h4>
                <div className="footer-social">
                  <button onClick={handleEmailClick} className="footer-social-link" title="Email">
                    <Mail size={20} />
                  </button>
                  <a href="https://www.linkedin.com/in/arthur-pazzutti-635ba4330/" target="_blank" rel="noopener noreferrer" className="footer-social-link" title="LinkedIn">
                    <Linkedin size={20} />
                  </a>
                  <a href="https://instagram.com/arthur.pazzutti" target="_blank" rel="noopener noreferrer" className="footer-social-link" title="Instagram">
                    <Instagram size={20} />
                  </a>
                  <a href="https://github.com/pazzutti" target="_blank" rel="noopener noreferrer" className="footer-social-link" title="GitHub">
                    <Github size={20} />
                  </a>
                </div>
              </div>
            </div>
          </div>

          <div className="footer-bottom">
            <p className="footer-copyright">
              © 2025 Pazzutti. {language === 'pt' ? 'Todos os direitos reservados.' : 'All rights reserved.'}
            </p>
            <div className="footer-tech">
              
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Portfolio;